/**
 * Pilet Loader Service
 *
 * Dynamically loads and initializes pilets (micro-frontends) without requiring
 * full Piral runtime. Provides a minimal PiletApi interface for pilets to register
 * their routes, menus, and tiles.
 */

import React from "react";
import type { QueryClient } from '@tanstack/react-query';
import { deviceRegistry } from "./deviceRegistry";
import { usePermissions } from "../hooks/usePermissions";
import type { RouteObject } from "react-router-dom";
import { risPublicApiClient, deviceServiceApiClient } from "../api/apiConfig";
import { shopifyService } from "../api/services/shopifyService";
import { getAuth0TokenGetter, apiService, DeviceFilter, type CameraEntitlementConfig } from "./apiService";
import {
  useSignalRContext,
  useSignalREvent,
  type SignalRApi,
} from "../types/signalR";
import { featureFlagService, type FlagTargetingOptions } from "./featureFlagService";
import { permissionService } from "./permissionService";
import { ENV_FLAG_MAP } from "../config/envFlagMap";
import { queryClient as sharedQueryClient } from "../queryClient";
import { convertPermissionsToDotNotation } from "../contexts/AuthContext";

// Simplified PiletApi interface that matches what pilets expect
export interface PiletApi {
  registerPage: (route: string, component: React.ComponentType<any>) => void;
  registerPublicPage: (route: string, component: React.ComponentType<any>) => void;
  registerMenu: (item: MenuItemConfig) => void;
  registerTile: (component: React.ComponentType<any>, options?: any) => void;
  registerHeaderAction?: (component: React.ComponentType<any>) => void;
  usePermissions: typeof usePermissions;
  hasPermission: (permission: string) => boolean;
  isPermissionAvailable: (permission: string) => boolean;
  /** Shared React Query client — pilets can call prefetchQuery() before their route mounts */
  queryClient: QueryClient;
  
  // Feature flags
  isFeatureEnabled: (flagKey: string, targetingOptions?: FlagTargetingOptions) => boolean;
  
  // Authentication & User (extended for Pro-Portal)
  getAccessToken: () => string | null;
  getAccessTokenSilently: () => Promise<string>; // Auth0 async token getter
  getCurrentUser: () => CurrentUser | null;
  /** Device types (e.g. "camera", "hvac") the current user has already seen FTUE tutorials for. */
  getFtueSeenDeviceTypes: () => Promise<string[]>;
  /** Marks a device type as seen so its FTUE tutorial is not shown again. */
  markFtueDeviceTypeSeen: (deviceType: string) => Promise<void>;
  getConfig: () => PiletConfig;
  getConsumerData: () => any | null;
  getRolePermissions: (context?: string) => Promise<any>;
  convertPermissionsToDotNotation: typeof convertPermissionsToDotNotation;
  /**
   * Invalidates the shell's consumer data cache (localStorage).
   * Call this after registering a new device so the next camera query
   * fetches fresh account/device data from the API instead of the stale cache.
   */
  refreshConsumerData: () => Promise<void>;
  getSelectedLocation: () => {
    id: string;
    name: string;
    consumerAccountId?: string;
  } | null;
  /**
   * Returns the per-device camera entitlement config map (deviceId -> camera
   * config). This is the single source of truth for subscription entitlements
   * exposed to pilets — both per-device gating (e.g. AI search's
   * aiClipSearch/aiSnapshotSearch flags) and scope-level "any device has X"
   * gating (via a pilet-side merge helper) are derived from this map.
   *
   * Returns `null` when the entitlement feature flag is disabled (pilots
   * should show all devices / features in that case).
   *
   * Per-device value semantics:
   *   - `undefined` (id not in map) → device wasn't in the API response;
   *     pilets should pass-through (show feature).
   *   - `null`                      → device WAS in the response but has no
   *     entitlement (e.g. plan lapsed); pilets should treat as "not entitled".
   *   - `CameraEntitlementConfig`   → normal per-device configuration.
   */
  getPerDeviceEntitlementConfig: () => Record<string, CameraEntitlementConfig | null> | null;
  
  meta: {
    deviceRegistry: typeof deviceRegistry;
    signalR: SignalRApi;
    [key: string]: any;
  };
  navigation: {
    push: (path: string) => void;
  };
  // API clients generated from OpenAPI specs
  api: {
    risPublic: typeof risPublicApiClient;
    deviceService: typeof deviceServiceApiClient;
    shopify: typeof shopifyService;
  };
}

export interface CurrentUser {
  id: string;
  role?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  accountId?: string;
  partnerAccountId?: string;
  photoUrl?: string;
}

export interface PiletConfig {
  risBaseUrl: string;
  signalRUrl?: string;
  googleMapsApiKey?: string;
  mediaServicesBaseUrl: string;
  featureFlags: Record<string, boolean>;
  vavApiUrl?: string;
  vavAuthApiUrl?: string;
  vavClientId?: string;
  vavCameraStateUrl?: string;
}

export interface MenuItemConfig {
  type?: string; // Made optional for new format
  label: string;
  icon?: React.ReactNode; // Support React components for icons
  target?: string; // Legacy format
  path?: string; // New shell format
  permission?: string; // New shell format (single permission)
  /** Called when the submenu opens so the pilet can prefetch data before the route mounts */
  onExpand?: () => void;
  requiredPermissions?: string[]; // Legacy format (array)
  requiredContext?: 'PARTNER' | 'CONSUMER'; // Context-based visibility
  submenu?: Array<{
    label: string;
    target?: string; // Legacy format
    path?: string; // New shell format
    permission?: string; // New shell format
    requiredPermissions?: string[]; // Legacy format
    /** Called on hover so the pilet can prefetch per-item media data (e.g. snapshots) */
    onHover?: () => void;
  }>;
}

export interface DashboardTileConfig {
  component: React.ComponentType<any>;
  options?: {
    requiredPermissions?: string[];
    order?: number;
  };
}

interface PiletModule {
  setup: (api: PiletApi) => void;
  metadata?: {
    moduleId: string;
    permissionsRequired?: string[];
  };
}

class PiletLoaderService {
  private routes: RouteObject[] = [];
  private publicRoutes: RouteObject[] = [];
  private menuItems: MenuItemConfig[] = [];
  private dashboardTiles: DashboardTileConfig[] = [];
  private headerActions: React.ComponentType<any>[] = [];
  private loadedPilets: Set<string> = new Set();
  private isLoading: boolean = false; // Guard to prevent concurrent loads

  /**
   * Create a PiletApi instance for a pilet to use during setup
   */
  private createPiletApi(): PiletApi {
    return {
      registerPage: (route: string, component: React.ComponentType<any>) => {
        // Wrap component in React element for React Router
        const Component = component;
        this.routes.push({
          path: route,
          element: <Component />,
        });
      },
      registerPublicPage: (route: string, component: React.ComponentType<any>) => {
        // Register a public route (no authentication required)
        const Component = component;
        this.publicRoutes.push({
          path: route,
          element: <Component />,
        });
      },
      registerMenu: (item: MenuItemConfig) => {
        this.menuItems.push(item);
      },
      registerTile: (component: React.ComponentType<any>, options?: any) => {
        this.dashboardTiles.push({ component, options });
      },
      registerHeaderAction: (component: React.ComponentType<any>) => {
        this.headerActions.push(component);
      },
      usePermissions,
      hasPermission: (permission: string) => permissionService.hasPermission(permission),
      isPermissionAvailable: (permission: string) => permissionService.isPermissionAvailable(permission),
      
      // Feature flag checking
      // Checks both flag systems:
      //   1. permissionService (VITE_FF_* keys mapped via ENV_FLAG_MAP, e.g. 'cameras.ai-search')
      //   2. featureFlagService (VITE_FLAG_* / VITE_FEATURE_* keys, e.g. 'CAMERA_SAVED_CLIPS')
      // permissionService.isFeatureEnabled returns false only when explicitly set to false;
      // featureFlagService.isEnabled returns false for unknown keys (opt-in).
      isFeatureEnabled: (flagKey: string, targetingOptions?: FlagTargetingOptions) => {
        // Check permissionService for VITE_FF_* keys (dot-notation keys in ENV_FLAG_MAP)
        const isEnvFfKey = Object.values(ENV_FLAG_MAP).includes(flagKey);
        if (isEnvFfKey) {
          return permissionService.isFeatureEnabled(flagKey);
        }
        // Fall back to featureFlagService (VITE_FLAG_* / VITE_FEATURE_* flags)
        const allFlags = featureFlagService.getAllFlags();
        if (flagKey in allFlags) {
          return featureFlagService.isEnabled(flagKey, targetingOptions);
        }
        // Unknown to both systems — default enabled
        return true;
      },
      
      // Extended authentication methods for Pro-Portal
      getAccessToken: () => {
        // Synchronous fallback - tries localStorage first
        const token = localStorage.getItem('authToken');
        return token;
      },
      
      getAccessTokenSilently: async () => {
        // Async Auth0 token getter - preferred method
        const tokenGetter = getAuth0TokenGetter();
        if (tokenGetter) {
          return await tokenGetter();
        }
        // Fallback to localStorage if Auth0 not available
        return localStorage.getItem('authToken') || '';
      },
      
      getCurrentUser: () => {
        // Get current user from localStorage or AuthContext
        const userStr = localStorage.getItem('currentUser');
        if (userStr) {
          try {
            const currentUser = JSON.parse(userStr) as CurrentUser;
            const storedUser = localStorage.getItem('oneportal_user');
            const selectedContext = localStorage.getItem('oneportal_selected_context');

            if (storedUser && selectedContext) {
              const userWithContexts = JSON.parse(storedUser) as {
                contexts?: Array<{ context?: string; roleName?: string }>;
              };
              const activeContext = userWithContexts.contexts?.find(
                (context) => context.context === selectedContext,
              );
              currentUser.role = activeContext?.roleName;
            }

            return currentUser;
          } catch {
            return null;
          }
        }
        return null;
      },

      getFtueSeenDeviceTypes: async () => {
        const userStr = localStorage.getItem('currentUser');
        const userId = userStr ? (JSON.parse(userStr) as CurrentUser)?.id : undefined;
        if (!userId) return [];
        const { ftueSettingsService } = await import('./ftueSettingsService');
        const settings = await ftueSettingsService.get(userId);
        return settings.seenDeviceTypes;
      },

      markFtueDeviceTypeSeen: async (deviceType: string) => {
        const userStr = localStorage.getItem('currentUser');
        const userId = userStr ? (JSON.parse(userStr) as CurrentUser)?.id : undefined;
        if (!userId) return;
        const { ftueSettingsService } = await import('./ftueSettingsService');
        const settings = await ftueSettingsService.get(userId);
        if (settings.seenDeviceTypes.includes(deviceType)) return;
        await ftueSettingsService.update(userId, {
          seenDeviceTypes: [...settings.seenDeviceTypes, deviceType],
        });
      },

      getConfig: () => {
        return {
          risBaseUrl: import.meta.env.VITE_RIS_BASE_URL || 'https://qa.api.honeywellhome.com/ris-public-api',
          signalRUrl: import.meta.env.VITE_SIGNALR_URL || '',
          googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '',
          mediaServicesBaseUrl: import.meta.env.VITE_MEDIA_SERVICES_URL || 'https://mediaservices.qa.titans.cloud/utility',
          featureFlags: featureFlagService.getAllFlags(),
          vavApiUrl: import.meta.env.VITE_VAV_API_URL ?? '',
          vavAuthApiUrl: import.meta.env.VITE_VAV_AUTH_API_URL || import.meta.env.VITE_VAV_AUTH_URL || '',
          vavClientId: import.meta.env.VITE_VAV_CLIENT_ID || 'c60600bbbb6b457389014efc9c206190',
          vavCameraStateUrl: import.meta.env.VITE_VAV_API_URL || '',
        };
      },

      getConsumerData: () => {
        try {
        const raw = localStorage.getItem("oneportal_consumer_data");
        return raw ? JSON.parse(raw) : null;
        } catch {
        return null;
        }
      },

      getRolePermissions: (context?: string) => apiService.getRolePermissions(context),
  convertPermissionsToDotNotation,

      refreshConsumerData: async () => {
        try {
          await apiService.forceRefreshToken();
          const context = localStorage.getItem("oneportal_selected_context") || undefined;
          const freshData = await apiService.getConsumerAccounts(context, DeviceFilter.CAMERAS);
          // Update the full copy so subsequent company switches rebuild the
          // scoped payload from fresh data.
          try {
            localStorage.setItem(
              "oneportal_consumer_data_full",
              JSON.stringify(freshData),
            );
          } catch {
            /* ignore storage errors */
          }
          // If the shell has scoped the pilet-facing consumer data to a
          // specific company (multi-company COMMERCIAL), preserve that scope
          // when rewriting `oneportal_consumer_data` — otherwise "My
          // Organization" would suddenly show devices from every company on
          // the account after the refresh.
          const rawIndex = localStorage.getItem("oneportal_selected_company_index");
          const companyIndex = rawIndex !== null ? Number(rawIndex) : NaN;
          const consumerUsers = freshData?.data?.consumerUsers ?? [];
          const shouldScope =
            Number.isInteger(companyIndex) &&
            companyIndex >= 0 &&
            companyIndex < consumerUsers.length;
          const payload = shouldScope
            ? {
                ...freshData,
                data: {
                  ...(freshData?.data ?? {}),
                  consumerUsers: [consumerUsers[companyIndex]],
                },
              }
            : freshData;
          localStorage.setItem("oneportal_consumer_data", JSON.stringify(payload));
          // Notify AuthContext so it can sync the locations state (LocationSelector depends on it)
          window.dispatchEvent(new CustomEvent('oneportal:consumer-data-refreshed', { detail: freshData }));
        } catch {
          // If the re-fetch fails, clear the stale cache so loadAllCameraData
          // falls back to calling /accounts itself.
          localStorage.removeItem("oneportal_consumer_data");
          localStorage.removeItem("oneportal_consumer_data_full");
        }
      },
      getSelectedLocation: () => {
        try {
        const raw = localStorage.getItem("oneportal_selected_location");
        return raw ? JSON.parse(raw) : null;
        } catch {
        return null;
        }
      },

      getPerDeviceEntitlementConfig: () => {
        // Single source of truth for subscription entitlements exposed to
        // pilets. Gated by the same `cameras.entitlement` feature flag: when
        // disabled, returns null (pilots should show all features).
        if (!permissionService.isFeatureEnabled('cameras.entitlement')) {
          return null;
        }
        try {
          const raw = localStorage.getItem("oneportal_entitlement_configs_by_device");
          if (!raw) return null;
          const parsed = JSON.parse(raw);
          return typeof parsed === "object" && parsed !== null
            ? (parsed as Record<string, CameraEntitlementConfig | null>)
            : null;
        } catch {
          return null;
        }
      },
          
      meta: {
        deviceRegistry,
        signalR: {
          useSignalRContext,
          useSignalREvent,
        },
      },
      navigation: {
        push: (path: string) => {
          window.location.href = path;
        },
      },
      // Expose auto-generated API clients to pilets
      api: {
        risPublic: risPublicApiClient,
        deviceService: deviceServiceApiClient,
        shopify: {
          ...shopifyService,
          // Explicitly expose metafield-rich query method
          getProductByHandleWithMetafields: shopifyService.getProductByHandleWithMetafields,
        },
      },
      // Shared React Query client — enables pilets to prefetchQuery() before their route mounts
      queryClient: sharedQueryClient,
    };
  }

  /**
   * Load a pilet module and call its setup function
   */
  async loadPilet(
    piletName: string,
    importFn: () => Promise<PiletModule>
  ): Promise<void> {
    if (this.loadedPilets.has(piletName)) {
      return;
    }

    try {
      const piletModule = await importFn();

      if (!piletModule.setup || typeof piletModule.setup !== "function") {
        return;
      }

      const api = this.createPiletApi();
      piletModule.setup(api);

      this.loadedPilets.add(piletName);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Load all configured pilets
   */
  async loadAllPilets(): Promise<void> {
    // Prevent duplicate loading (React StrictMode calls useEffect twice)
    if (this.isLoading || this.loadedPilets.size > 0) {
      console.log("⚠️ Pilets already loaded or loading in progress, skipping duplicate load");
      return;
    }
    
    this.isLoading = true;
    console.log("🔄 Loading pilets...");

    // Load all pilets in parallel — previously sequential awaits meant every pilet
    // waited for the one before it, delaying dashboard and other early routes.
    const pilets: Array<{ name: string; importFn: () => Promise<PiletModule> }> = [
      { name: "camera-pilet",       importFn: () => import("@resideo/camera-pilet") },
      { name: "dashboard-pilet",    importFn: () => import("@resideo/dashboard-pilet") },
      { name: "pro-portal-pilet",   importFn: () => import("@resideo/pro-portal-pilet") },
      { name: "billing-pilet",      importFn: () => import("@resideo/billing-pilet") },
      { name: "notification-pilet", importFn: () => import("@resideo/notification-pilet") },
      { name: "thermostat-pilet",   importFn: () => import("@resideo/thermostat-pilet") },
    ];

    await Promise.allSettled(
      pilets.map(({ name, importFn }) =>
        this.loadPilet(name, importFn)
          .then(() => console.log(`✅ ${name} loaded successfully`))
          .catch((error) => { console.warn(`⚠️ ${name} failed to load:`, error); })
      )
    );

    console.log(`📊 Total pilets loaded: ${this.loadedPilets.size}`);
    console.log(`📋 Total routes registered: ${this.routes.length}`);
    console.log(`🎯 Total menu items registered: ${this.menuItems.length}`);

    this.isLoading = false;

    // Add other pilets here as they're developed
    // { name: 'panel-pilet',      importFn: () => import('@resideo/panel-pilet') },
  }

  /**
   * Get all registered routes from loaded pilets
   */
  getRoutes(): RouteObject[] {
    return [...this.routes];
  }

  /**
   * Get all registered public routes (no auth required) from loaded pilets
   */
  getPublicRoutes(): RouteObject[] {
    return [...this.publicRoutes];
  }

  /**
   * Get all registered menu items from loaded pilets
   */
  getMenuItems(): MenuItemConfig[] {
    return [...this.menuItems];
  }

  /**
   * Get all registered dashboard tiles from loaded pilets
   */
  getDashboardTiles(): DashboardTileConfig[] {
    return [...this.dashboardTiles];
  }

  /**
   * Get all registered header actions from loaded pilets
   */
  getHeaderActions(): React.ComponentType<any>[] {
    return [...this.headerActions];
  }

  /**
   * Clear all loaded pilets (useful for testing/hot reload)
   */
  clear(): void {
    this.routes = [];
    this.publicRoutes = [];
    this.menuItems = [];
    this.dashboardTiles = [];
    this.headerActions = [];
    this.loadedPilets.clear();
  }
}

// Export singleton instance
export const piletLoader = new PiletLoaderService();